// libraries/components/src/components/upcoming-trips/services/itinerary-state-transform.ts

import type {
  ItineraryServiceResponse,
  ItineraryState,
  PnrItinAirSegState,
} from "@jb-manage-trips/types/itinerary";

export function getTripCode(originOrDestination: string): string {
  const regex = /\(([^)]+)\)/;
  const matches = regex.exec(originOrDestination);
  return matches?.[1] ?? originOrDestination;
}
export type ItineraryImageOptions = {
  cmsHostname: string;
  flightBlockImagePrefix: string;
};
export function getItineraryImage(
  destinationCode: string | undefined,
  options: ItineraryImageOptions,
): string {
  if (!destinationCode) {
    return "";
  }

  const { cmsHostname, flightBlockImagePrefix } = options;
  const prefix = flightBlockImagePrefix.replace(/^\/+/, "");
  const path = `${prefix}/${destinationCode}.jpg`;

  return `https://${cmsHostname}/${path}`.replace("https:///", "https://");
}

function emptySegment(): PnrItinAirSegState {
  return {
    classOfService: "",
    origin: "",
    destination: "",
    scheduledDepartureTime: "",
    scheduledArrivalTime: "",
    seatNumber: "",
    durationInMinutes: 0,
    flightNumber: 0,
    operatingAirlineCode: "",
    operatingAirlineShortName: "",
    equipmentType: "",
    cabinClass: "",
    originCode: "",
    destinationCode: "",
  };
}

/**
 * New behavior:
 * - normalize every segment with originCode / destinationCode
 * - keep tripSummary as a shell-level summary only
 * - UI flight-card rendering is expected to come from pnrItinAirSeg, not tripSummary
 */
export function itineraryStateTransform(
  itineraries: ItineraryServiceResponse[],
  imageOptions: ItineraryImageOptions,
): ItineraryState[] {
  return itineraries.map((itinerary: ItineraryServiceResponse) => {
    const normalizedSegments: PnrItinAirSegState[] =
      itinerary.pnrItinAirSeg.map((segment) => ({
        ...emptySegment(),
        ...segment,
        originCode: getTripCode(segment.origin),
        destinationCode: getTripCode(segment.destination),
      }));

    if (normalizedSegments.length === 0) {
      return {
        ...itinerary,
        pnrItinAirSeg: [],
        tripSummary: emptySegment(),
        tripImage: "",
        tripDurationInMinutes: 0,
      };
    }

    const firstSegment = normalizedSegments[0];
    const lastSegment = normalizedSegments.at(-1);

    if (!firstSegment || !lastSegment) {
      return {
        ...itinerary,
        pnrItinAirSeg: [],
        tripSummary: emptySegment(),
        tripImage: "",
        tripDurationInMinutes: 0,
      };
    }

    const tripDurationInMinutes = normalizedSegments.reduce(
      (total, segment) => total + segment.durationInMinutes,
      0,
    );

    /**
     * Shell-level summary:
     * - origin anchors to the first leg
     * - destination anchors to the first leg's destination for shell imagery/title
     * - overall date range can still be inferred elsewhere from full segment list
     */
    const tripSummary: PnrItinAirSegState = {
      ...firstSegment,
      scheduledArrivalTime: lastSegment.scheduledArrivalTime,
      durationInMinutes: tripDurationInMinutes,
    };

    return {
      ...itinerary,
      pnrItinAirSeg: normalizedSegments,
      tripSummary,
      tripImage: getItineraryImage(firstSegment.destinationCode, imageOptions),
      tripDurationInMinutes,
    };
  });
}
